import React from "react";
import { Link } from "react-router-dom";
import { X, Trash2, Plus, Minus, Sparkles } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatRON, computeDiscount, bestQtyTier } from "../lib/shop";
import { useT } from "../lib/i18n";

export default function CartDrawer() {
  const { open, setOpen, items, update, remove, subtotal, grossSubtotal, lineDiscountTotal } = useCart();
  const { t } = useT();
  const discount = computeDiscount(subtotal);

  React.useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white border-l border-gray-200 flex flex-col">
        <div className="flex items-center justify-between h-20 px-6 border-b border-gray-200">
          <h3 className="font-display text-xl font-black tracking-tight">{t("cart.title")}</h3>
          <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-100" data-testid="close-cart"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center text-gray-500 mt-20" data-testid="cart-empty">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-3">{t("cart.empty.label")}</p>
              <p>{t("cart.empty")}</p>
              <button onClick={() => setOpen(false)} className="mt-6 inline-block border border-gray-900 px-6 py-3 text-sm font-semibold hover:bg-gray-900 hover:text-white transition">
                {t("cart.empty.cta")}
              </button>
            </div>
          ) : items.map((it) => {
            const tier = bestQtyTier(it.tiers, it.quantity);
            return (
            <div key={it.product_id} className="flex gap-4 p-6 border-b border-gray-200" data-testid={`cart-item-${it.product_id}`}>
              <div className="w-20 h-20 bg-gray-50 border border-gray-200 flex-shrink-0 overflow-hidden">
                <img src={it.image} alt={it.name} className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-sm leading-snug">{it.name}</p>
                  <button onClick={() => remove(it.product_id)} className="text-gray-400 hover:text-red-600" data-testid={`remove-${it.product_id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1 font-mono">{formatRON(it.price)}</p>
                {tier.percent > 0 && (
                  <p className="text-[10px] text-[#188f42] font-bold uppercase tracking-wider mt-1.5 inline-flex items-center gap-1" data-testid={`line-tier-${it.product_id}`}>
                    <Sparkles size={11} strokeWidth={1.5} /> −{tier.percent}% {t("cart.disc.for")} {it.quantity} {t("cart.disc.pieces")}
                  </p>
                )}
                <div className="mt-3 inline-flex items-center border border-gray-300">
                  <button className="px-2 py-1 hover:bg-gray-100" onClick={() => update(it.product_id, it.quantity - 1)} data-testid={`dec-${it.product_id}`}><Minus size={14} /></button>
                  <span className="px-3 text-sm font-medium" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                  <button className="px-2 py-1 hover:bg-gray-100" onClick={() => update(it.product_id, it.quantity + 1)} data-testid={`inc-${it.product_id}`}><Plus size={14} /></button>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div className="border-t border-gray-200 p-6 space-y-4">
            {discount.applied ? (
              <>
                <div className="border border-[#1ebe57] bg-[#1ebe57]/8 px-4 py-3 flex items-start gap-3" data-testid="discount-applied-cart">
                  <Sparkles size={18} strokeWidth={1.5} className="text-[#1ebe57] flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-bold text-[#188f42] uppercase tracking-wider">{t("cart.disc.applied")}</p>
                    <p className="text-gray-600 mt-1 font-mono">−{formatRON(discount.amount)}</p>
                  </div>
                </div>
                <div className="border border-dashed border-gray-900 bg-white px-4 py-3 flex items-start gap-3" data-testid="future-code-hint">
                  <span className="text-base">🎁</span>
                  <p className="text-[11px] text-gray-700 leading-relaxed">{t("cart.future.code")}</p>
                </div>
              </>
            ) : (
              <div className="border border-dashed border-gray-300 bg-gray-50 px-4 py-3" data-testid="discount-remaining-cart">
                <p className="text-xs text-gray-600 leading-relaxed">
                  {t("cart.disc.remaining")} <span className="font-mono font-bold text-gray-900">{formatRON(discount.remaining)}</span> {t("cart.disc.toGet")}
                </p>
                <div className="mt-2 h-1 bg-gray-200 overflow-hidden">
                  <div className="h-full bg-[#1ebe57] transition-all" style={{ width: `${Math.min(100, (subtotal / 500) * 100)}%` }} />
                </div>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t("cart.subtotal")}</span>
              <span className="font-mono font-semibold" data-testid="cart-subtotal">{formatRON(grossSubtotal)}</span>
            </div>
            {lineDiscountTotal > 0 && (
              <div className="flex justify-between text-sm text-[#188f42]" data-testid="cart-line-discount">
                <span>{t("cart.disc.qty")}</span>
                <span className="font-mono font-semibold">−{formatRON(lineDiscountTotal)}</span>
              </div>
            )}
            {discount.applied && (
              <div className="flex justify-between text-sm text-[#188f42]">
                <span>{t("cart.disc.order")} ({discount.percent}%)</span>
                <span className="font-mono font-semibold">−{formatRON(discount.amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t("cart.shipping")}</span>
              <span className="font-mono">{t("cart.shipping.calc")}</span>
            </div>
            <Link to="/checkout" onClick={() => setOpen(false)}
              className="block w-full text-center bg-gray-900 text-white px-6 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-gray-800 transition" data-testid="goto-checkout">
              {t("cart.checkout")}
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
}
